import i18n from "../i18n";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { farmCalendarParts, farmDateRangeForPreset } from "@/lib/farmTime";
import { formatCurrency } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PayrollTd, PayrollTh, hoursLabel } from "@/components/payroll/PayrollUi";
import { Wallet, Plus, Printer, Calculator, Settings2, FileSpreadsheet, History, ReceiptText } from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const PERIOD_TYPES = ["monthly", "semi-monthly", "weekly", "daily"];
const REPORT_TYPES = [
    { value: "payroll_summary", label: "Payroll Summary" },
    { value: "payroll_register", label: "Payroll Register" },
    { value: "overtime_report", label: "Overtime Report" },
    { value: "late_report", label: "Late Report" },
    { value: "absent_report", label: "Absent Report" },
];

export default function PayrollPage() {
    const farmNow = farmCalendarParts();
    const [tab, setTab] = useState("dashboard");
    const [year, setYear] = useState(farmNow.year);
    const [month, setMonth] = useState(farmNow.month);
    const [runs, setRuns] = useState([]);
    const [payslips, setPayslips] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [payrollRows, setPayrollRows] = useState([]);
    const [workforceSummary, setWorkforceSummary] = useState({});
    const [attendanceSettings, setAttendanceSettings] = useState(null);
    const [settingsForm, setSettingsForm] = useState(null);
    const [reportType, setReportType] = useState("payroll_summary");
    const [reportRows, setReportRows] = useState([]);
    const [reportSummary, setReportSummary] = useState({});
    const [dlg, setDlg] = useState(null);

    const loadCore = useCallback(async () => {
        try {
            const [runData, payslipData, employeeData, payrollData, settingsData] = await Promise.all([
                api.get("/payroll-runs").then((r) => r.data),
                api.get("/payslips").then((r) => r.data),
                api.get("/employees").then((r) => r.data).catch(() => []),
                api.get("/attendance/payroll", { params: { year, month } }).then((r) => r.data),
                api.get("/attendance/settings").then((r) => r.data),
            ]);
            setRuns(runData || []);
            setPayslips(payslipData || []);
            setEmployees(employeeData || []);
            setPayrollRows(Array.isArray(payrollData?.rows) ? payrollData.rows : []);
            setWorkforceSummary(payrollData?.workforce || {});
            setAttendanceSettings(settingsData || null);
            setSettingsForm(settingsData || null);
        } catch (err) {
            toast.error("Failed to load payroll module");
        }
    }, [year, month]);

    const loadReports = useCallback(async () => {
        try {
            const { data } = await api.get("/attendance/reports", {
                params: { report_type: reportType, year, month },
            });
            setReportRows(Array.isArray(data?.rows) ? data.rows : []);
            setReportSummary(data?.summary || {});
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to load payroll reports");
            setReportRows([]);
            setReportSummary({});
        }
    }, [reportType, year, month]);

    useEffect(() => {
        loadCore();
    }, [loadCore]);

    useEffect(() => {
        if (tab === "reports") loadReports();
    }, [tab, loadReports]);

    const saveSettings = async () => {
        try {
            const { data } = await api.put("/attendance/settings", settingsForm);
            setAttendanceSettings(data);
            setSettingsForm(data);
            toast.success("Payroll settings saved");
            await loadCore();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to save payroll settings");
        }
    };

    const save = async (path, body) => {
        try {
            const isPayslip = path === "payslips";
            const targetPath = isPayslip ? "payslips/secure" : path;
            if (body.id) {
                const { id, ...rest } = body;
                await api.put(`/${targetPath}/${id}`, rest);
            } else {
                await api.post(`/${targetPath}`, body);
            }
            toast.success("Saved");
            setDlg(null);
            loadCore();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
        }
    };

    const del = async (path, id, label) => {
        if (!window.confirm(`Delete ${label}?`)) return;
        try {
            await api.delete(`/${path}/${id}`);
            toast.success("Deleted");
            loadCore();
        } catch {
            toast.error("Delete failed");
        }
    };

    const payrollSummary = useMemo(() => {
        const gross = payrollRows.reduce((sum, row) => sum + Number(row.gross_pay || 0), 0);
        const net = payrollRows.reduce((sum, row) => sum + Number(row.net_pay || 0), 0);
        const pending = payrollRows.filter((row) => row.approval_status !== "approved").length;
        const pendingOT = payrollRows.reduce((sum, row) => sum + Number(row.pending_overtime_hours || 0), 0);
        return { gross, net, pending, pendingOT, employees: payrollRows.length };
    }, [payrollRows]);

    const exportReportExcel = async () => {
        const XLSX = await import("xlsx");
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(reportRows || []);
        XLSX.utils.book_append_sheet(wb, ws, "Payroll Report");
        const buf = XLSX.write(wb, { type: "array", bookType: "xlsx" });
        const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `payroll-report-${reportType}-${year}-${String(month).padStart(2, "0")}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    const exportReportPdf = () => {
        const doc = new jsPDF({ orientation: "landscape" });
        doc.setFontSize(14);
        doc.text(`Payroll Report - ${reportType}`, 14, 16);
        const body = (reportRows || []).map((row) => [
            row.employee_name || row.employee || "-",
            row.date || row.period || `${year}-${String(month).padStart(2, "0")}`,
            row.pay_type || row.status || "-",
            row.gross_pay == null ? "-" : formatCurrency(row.gross_pay, attendanceSettings?.payroll_currency || "PHP"),
            row.net_pay == null ? "-" : formatCurrency(row.net_pay, attendanceSettings?.payroll_currency || "PHP"),
            row.approval_status || "-",
        ]);
        autoTable(doc, {
            startY: 24,
            head: [["Employee", "Date/Period", "Type", "Gross", "Net", "Status"]],
            body,
            styles: { fontSize: 8 },
            headStyles: { fillColor: [46, 125, 50] },
        });
        doc.save(`payroll-report-${reportType}-${year}-${String(month).padStart(2, "0")}.pdf`);
    };

    const runMap = useMemo(() => Object.fromEntries((runs || []).map((r) => [r.id, r])), [runs]);
    const empMap = useMemo(() => Object.fromEntries((employees || []).map((e) => [e.id, e])), [employees]);

    return (
        <div className="space-y-6" data-testid="payroll-page">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="tiny-label flex items-center gap-2"><Wallet className="w-4 h-4" />{i18n.t("batch1.d45987441aa0")}</div>
                    <h1 className="text-3xl font-bold mt-1">{i18n.t("batch1.837e09fce002")}</h1>
                    <p className="text-sm text-muted-foreground">{i18n.t("batch1.a3d15e89dcb8")}</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <Button variant="outline" onClick={() => setDlg({ kind: "calc", data: {} })} data-testid="quick-calc-btn">
                        <Calculator className="w-4 h-4 mr-1" />{i18n.t("batch1.505b07c067ac")}
                    </Button>
                    <Button onClick={() => setDlg({ kind: "run", data: {} })} data-testid="new-run-btn">
                        <Plus className="w-4 h-4 mr-1" />{i18n.t("batch1.fca608e68261")}
                    </Button>
                </div>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="h-auto flex flex-wrap" data-testid="payroll-tabs">
                    <TabsTrigger value="dashboard">{i18n.t("batch1.c1b4d92cd3af")}</TabsTrigger>
                    <TabsTrigger value="review">{i18n.t("batch1.237003634970")}</TabsTrigger>
                    <TabsTrigger value="settings">{i18n.t("batch1.bd2f8874cb1a")}</TabsTrigger>
                    <TabsTrigger value="rates">{i18n.t("batch1.3168162b6ccd")}</TabsTrigger>
                    <TabsTrigger value="deductions">{i18n.t("batch1.757bbae2b303")}</TabsTrigger>
                    <TabsTrigger value="allowances">{i18n.t("batch1.60a83b2a7fc8")}</TabsTrigger>
                    <TabsTrigger value="payslips">{i18n.t("batch1.6d3aec5a7cf6")}</TabsTrigger>
                    <TabsTrigger value="history">{i18n.t("batch1.b278e65545af")}</TabsTrigger>
                    <TabsTrigger value="reports">{i18n.t("batch1.66257651cd9f")}</TabsTrigger>
                </TabsList>

                <TabsContent value="dashboard" className="space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
                        <Kpi label={i18n.t("batch1.36bd682e26f2")} value={workforceSummary.active_employee_count || 0} testid="kpi-active-workforce" />
                        <Kpi label={i18n.t("batch1.1929b59a2c7a")} value={payrollSummary.employees} testid="kpi-payroll-employees" />
                        <Kpi label={i18n.t("batch1.048188f452d4")} value={formatCurrency(payrollSummary.gross, attendanceSettings?.payroll_currency || "PHP")} testid="kpi-payroll-gross" />
                        <Kpi label={i18n.t("batch1.cafacb251b01")} value={formatCurrency(payrollSummary.net, attendanceSettings?.payroll_currency || "PHP")} testid="kpi-payroll-net" tone="green" />
                        <Kpi label={i18n.t("batch1.8ad7743ad473")} value={payrollSummary.pending} testid="kpi-payroll-pending" tone="red" />
                        <Kpi label={i18n.t("batch1.6881cf0ce1e8")} value={hoursLabel(payrollSummary.pendingOT)} testid="kpi-payroll-pending-ot" />
                    </div>
                </TabsContent>

                <TabsContent value="review" className="space-y-4">
                    <Card className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                            <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value || farmNow.year))} className="h-11" />
                            <Input type="number" value={month} min="1" max="12" onChange={(e) => setMonth(Number(e.target.value || 1))} className="h-11" />
                            <Button className="h-11" onClick={loadCore}>{i18n.t("batch1.ddcb77ff769e")}</Button>
                            <Button className="h-11" variant="outline" onClick={exportReportPdf}><Printer className="w-4 h-4 mr-2" />{i18n.t("batch1.3dd7d56a8ed3")}</Button>
                        </div>
                    </Card>
                    <Card className="p-0 overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-secondary/60 text-xs uppercase text-muted-foreground">
                                <tr>
                                    <PayrollTh>{i18n.t("batch1.079711ea16f3")}</PayrollTh><PayrollTh>{i18n.t("batch1.29353c2b2528")}</PayrollTh><PayrollTh>{i18n.t("batch1.fe3d54900d91")}</PayrollTh><PayrollTh>{i18n.t("batch1.26ede189a778")}</PayrollTh><PayrollTh>{i18n.t("batch1.6beef1dfb098")}</PayrollTh><PayrollTh>{i18n.t("batch1.05b1368a3a3a")}</PayrollTh><PayrollTh>{i18n.t("batch1.6881cf0ce1e8")}</PayrollTh><PayrollTh>{i18n.t("batch1.cf25e1aabf83")}</PayrollTh><PayrollTh>{i18n.t("batch1.90b257791180")}</PayrollTh><PayrollTh>{i18n.t("batch1.7f5e5b69c2ee")}</PayrollTh><PayrollTh>{i18n.t("batch1.e0d973c01708")}</PayrollTh><PayrollTh>{i18n.t("batch1.60a83b2a7fc8")}</PayrollTh><PayrollTh>{i18n.t("batch1.e144f64b4481")}</PayrollTh><PayrollTh>{i18n.t("batch1.f5dec71ed241")}</PayrollTh><PayrollTh>{i18n.t("batch1.cafacb251b01")}</PayrollTh><PayrollTh>{i18n.t("batch1.bae7d5be7082")}</PayrollTh><PayrollTh>{i18n.t("batch1.97c89a4d6630")}</PayrollTh>
                                </tr>
                            </thead>
                            <tbody>
                                {payrollRows.map((row) => (
                                    <tr key={row.employee_id} className="border-t">
                                        <PayrollTd>{row.employee_name || "-"}</PayrollTd>
                                        <PayrollTd>{row.monthly_salary ? formatCurrency(row.monthly_salary, row.currency || attendanceSettings?.payroll_currency || "PHP") : "-"}</PayrollTd>
                                        <PayrollTd>{row.daily_rate == null ? "-" : formatCurrency(row.daily_rate, row.currency || attendanceSettings?.payroll_currency || "PHP")}</PayrollTd>
                                        <PayrollTd>{row.hourly_rate == null ? "-" : formatCurrency(row.hourly_rate, row.currency || attendanceSettings?.payroll_currency || "PHP")}</PayrollTd>
                                        <PayrollTd>{hoursLabel(row.regular_hours)}</PayrollTd>
                                        <PayrollTd>{hoursLabel(row.approved_overtime_hours || row.overtime_hours)}</PayrollTd>
                                        <PayrollTd>{hoursLabel(row.pending_overtime_hours)}</PayrollTd>
                                        <PayrollTd>{formatCurrency(row.late_deduction || 0, row.currency || attendanceSettings?.payroll_currency || "PHP")}</PayrollTd>
                                        <PayrollTd>{formatCurrency(row.absent_deduction || 0, row.currency || attendanceSettings?.payroll_currency || "PHP")}</PayrollTd>
                                        <PayrollTd>{formatCurrency(row.holiday_pay || 0, row.currency || attendanceSettings?.payroll_currency || "PHP")}</PayrollTd>
                                        <PayrollTd>{formatCurrency(row.night_differential_pay || 0, row.currency || attendanceSettings?.payroll_currency || "PHP")}</PayrollTd>
                                        <PayrollTd>{formatCurrency(row.allowances || 0, row.currency || attendanceSettings?.payroll_currency || "PHP")}</PayrollTd>
                                        <PayrollTd>{formatCurrency(row.bonuses || 0, row.currency || attendanceSettings?.payroll_currency || "PHP")}</PayrollTd>
                                        <PayrollTd>{formatCurrency(row.other_deductions || 0, row.currency || attendanceSettings?.payroll_currency || "PHP")}</PayrollTd>
                                        <PayrollTd>{row.net_pay == null ? "-" : formatCurrency(row.net_pay, row.currency || attendanceSettings?.payroll_currency || "PHP")}</PayrollTd>
                                        <PayrollTd>{row.approval_status || "pending"}</PayrollTd>
                                        <PayrollTd><Button size="sm" variant="outline" onClick={() => printPayrollSlip(row)}>{i18n.t("batch1.34465c52157a")}</Button></PayrollTd>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Card>
                </TabsContent>

                <TabsContent value="settings" className="space-y-4">
                    <Card className="p-4">
                        <div className="flex items-center gap-2 mb-3 text-sm font-semibold"><Settings2 className="w-4 h-4" />{i18n.t("batch1.bd2f8874cb1a")}</div>
                        {settingsForm ? (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                {[["standard_working_days_per_month", "Working Days"], ["standard_hours_per_day", "Hours Per Day"], ["grace_period_minutes", "Grace Period"], ["lunch_allowed_minutes", "Lunch Minutes"], ["morning_break_allowed_minutes", "Morning Break"], ["afternoon_break_allowed_minutes", "Afternoon Break"], ["overtime_multiplier", "Overtime Multiplier"], ["holiday_multiplier", "Holiday Multiplier"], ["rest_day_multiplier", "Rest Day Multiplier"], ["night_differential_percent", "Night Differential %"], ["tax_percent", "Tax %"], ["sss_percent", "SSS %"], ["philhealth_percent", "PhilHealth %"], ["pagibig_percent", "Pag-IBIG %"]].map(([key, label]) => (
                                    <div key={key}>
                                        <Label className="tiny-label">{label}</Label>
                                        <Input value={settingsForm[key] ?? ""} onChange={(e) => setSettingsForm({ ...settingsForm, [key]: e.target.value })} className="h-10 mt-2" />
                                    </div>
                                ))}
                                <div>
                                    <Label className="tiny-label">{i18n.t("batch1.e070de224434")}</Label>
                                    <Input value={settingsForm.payroll_currency || "PHP"} onChange={(e) => setSettingsForm({ ...settingsForm, payroll_currency: e.target.value.toUpperCase() })} className="h-10 mt-2" />
                                </div>
                                <div>
                                    <Label className="tiny-label">{i18n.t("batch1.d1f7dc891b5e")}</Label>
                                    <Input value="Asia/Manila" readOnly aria-readonly="true" className="h-10 mt-2" />
                                </div>
                                {[["shift_arrival_time", "Shift Arrival"], ["workday_start_time", "Clock In"], ["morning_break_start_time", "Morning Break Start"], ["morning_break_end_time", "Morning Break End"], ["lunch_start_time", "Lunch Start"], ["lunch_end_time", "Lunch End"], ["afternoon_break_start_time", "Afternoon Break Start"], ["afternoon_break_end_time", "Afternoon Break End"], ["workday_end_time", "Clock Out"], ["shift_departure_time", "Shift Departure"]].map(([key, label]) => (
                                    <div key={key}>
                                        <Label className="tiny-label">{label}</Label>
                                        <Input type="time" value={settingsForm[key] || ""} onChange={(e) => setSettingsForm({ ...settingsForm, [key]: e.target.value })} className="h-10 mt-2" />
                                    </div>
                                ))}
                                <div>
                                    <Label className="tiny-label">{i18n.t("batch1.d9ad7bd07d54")}</Label>
                                    <Select value={settingsForm.morning_break_paid ? "paid" : "unpaid"} onValueChange={(v) => setSettingsForm({ ...settingsForm, morning_break_paid: v === "paid" })}>
                                        <SelectTrigger className="h-10 mt-2"><SelectValue /></SelectTrigger>
                                        <SelectContent><SelectItem value="paid">{i18n.t("batch1.dc9d4584a554")}</SelectItem><SelectItem value="unpaid">{i18n.t("batch1.50cc12f69e8f")}</SelectItem></SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="tiny-label">{i18n.t("batch1.44f285821581")}</Label>
                                    <Select value={settingsForm.lunch_paid ? "paid" : "unpaid"} onValueChange={(v) => setSettingsForm({ ...settingsForm, lunch_paid: v === "paid" })}>
                                        <SelectTrigger className="h-10 mt-2"><SelectValue /></SelectTrigger>
                                        <SelectContent><SelectItem value="paid">{i18n.t("batch1.dc9d4584a554")}</SelectItem><SelectItem value="unpaid">{i18n.t("batch1.50cc12f69e8f")}</SelectItem></SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="tiny-label">{i18n.t("batch1.e92ffaf3bfae")}</Label>
                                    <Select value={settingsForm.afternoon_break_paid ? "paid" : "unpaid"} onValueChange={(v) => setSettingsForm({ ...settingsForm, afternoon_break_paid: v === "paid" })}>
                                        <SelectTrigger className="h-10 mt-2"><SelectValue /></SelectTrigger>
                                        <SelectContent><SelectItem value="paid">{i18n.t("batch1.dc9d4584a554")}</SelectItem><SelectItem value="unpaid">{i18n.t("batch1.50cc12f69e8f")}</SelectItem></SelectContent>
                                    </Select>
                                </div>
                            </div>
                        ) : null}
                        <Button className="mt-4" onClick={saveSettings}>{i18n.t("batch1.0022a3e10caa")}</Button>
                    </Card>
                </TabsContent>

                <TabsContent value="rates" className="space-y-4">
                    <EmployeeCompTable rows={employees} currency={attendanceSettings?.payroll_currency || "PHP"} title={i18n.t("batch1.3168162b6ccd")} />
                </TabsContent>

                <TabsContent value="deductions" className="space-y-4">
                    <EmployeeCompTable rows={employees} currency={attendanceSettings?.payroll_currency || "PHP"} title={i18n.t("batch1.757bbae2b303")} mode="deductions" />
                </TabsContent>

                <TabsContent value="allowances" className="space-y-4">
                    <EmployeeCompTable rows={employees} currency={attendanceSettings?.payroll_currency || "PHP"} title={i18n.t("batch1.60a83b2a7fc8")} mode="allowances" />
                </TabsContent>

                <TabsContent value="payslips" className="space-y-4">
                    <div className="flex justify-end">
                        <Button onClick={() => setDlg({ kind: "payslip", data: {} })} data-testid="new-payslip-btn">
                            <Plus className="w-4 h-4 mr-1" />{i18n.t("batch1.ac746d1a1538")}
                        </Button>
                    </div>
                    <Card className="overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-muted/50"><tr className="text-left"><PayrollTh>{i18n.t("batch1.079711ea16f3")}</PayrollTh><PayrollTh>{i18n.t("batch1.170a28a9db6d")}</PayrollTh><PayrollTh>{i18n.t("batch1.9580a6176fdb")}</PayrollTh><PayrollTh>{i18n.t("batch1.757bbae2b303")}</PayrollTh><PayrollTh>{i18n.t("batch1.9bb81c2eccbe")}</PayrollTh><PayrollTh></PayrollTh></tr></thead>
                            <tbody>
                                {payslips.length === 0 && <tr><PayrollTd colSpan={6} className="text-center text-muted-foreground py-6">{i18n.t("batch1.f4f29aecc894")}</PayrollTd></tr>}
                                {payslips.slice().sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")).map((p) => {
                                    const emp = empMap[p.employee_id];
                                    const run = runMap[p.run_id];
                                    return (
                                        <tr key={p.id} className="border-t" data-testid={`payslip-row-${p.id}`}>
                                            <td className="p-2 font-medium">{emp?.name || p.employee_name || "—"}</td>
                                            <td className="p-2 text-xs">{run ? `${run.period_start} → ${run.period_end}` : (p.period || "—")}</td>
                                            <td className="p-2">{formatCurrency(p.gross_pay)}</td>
                                            <td className="p-2 text-destructive">{formatCurrency(p.total_deductions)}</td>
                                            <td className="p-2 font-semibold">{formatCurrency(p.net_pay)}</td>
                                            <td className="p-2 text-right whitespace-nowrap">
                                                <Button size="sm" variant="ghost" onClick={() => printPayslip(p, emp, run)} data-testid={`print-payslip-${p.id}`}><Printer className="w-3.5 h-3.5" /></Button>
                                                <Button size="sm" variant="ghost" onClick={() => setDlg({ kind: "payslip", data: p })}><ReceiptText className="w-3.5 h-3.5" /></Button>
                                                <Button size="sm" variant="ghost" onClick={() => del("payslips", p.id, "payslip")}>{i18n.t("batch1.f6fdbe48dc54")}</Button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </Card>
                </TabsContent>

                <TabsContent value="history" className="space-y-4">
                    <Card className="overflow-hidden">
                        <div className="p-4 flex items-center gap-2 text-sm font-semibold"><History className="w-4 h-4" />{i18n.t("batch1.b278e65545af")}</div>
                        <table className="w-full text-sm">
                            <thead className="bg-muted/50"><tr className="text-left"><PayrollTh>{i18n.t("batch1.170a28a9db6d")}</PayrollTh><PayrollTh>{i18n.t("batch1.3deb74565196")}</PayrollTh><PayrollTh>{i18n.t("batch1.80aa96da6777")}</PayrollTh><PayrollTh>{i18n.t("batch1.4029af0ce98d")}</PayrollTh><PayrollTh>{i18n.t("batch1.bae7d5be7082")}</PayrollTh></tr></thead>
                            <tbody>
                                {runs.map((r) => {
                                    const runPayslips = payslips.filter((p) => p.run_id === r.id);
                                    const totalNet = runPayslips.reduce((s, p) => s + (Number(p.net_pay) || 0), 0);
                                    return (
                                        <tr key={r.id} className="border-t">
                                            <PayrollTd>{r.period_start || "—"} → {r.period_end || "—"}</PayrollTd>
                                            <PayrollTd>{r.period_type || "monthly"}</PayrollTd>
                                            <PayrollTd>{runPayslips.length}</PayrollTd>
                                            <PayrollTd>{formatCurrency(totalNet)}</PayrollTd>
                                            <PayrollTd><Badge variant={r.status === "paid" ? "default" : "secondary"}>{r.status || "draft"}</Badge></PayrollTd>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </Card>
                </TabsContent>

                <TabsContent value="reports" className="space-y-4">
                    <Card className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                            <Select value={reportType} onValueChange={setReportType}>
                                <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                                <SelectContent>{REPORT_TYPES.map((item) => <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>)}</SelectContent>
                            </Select>
                            <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value || farmNow.year))} className="h-11" />
                            <Input type="number" min="1" max="12" value={month} onChange={(e) => setMonth(Number(e.target.value || 1))} className="h-11" />
                            <Button className="h-11" onClick={loadReports}>{i18n.t("batch1.ddcb77ff769e")}</Button>
                            <div className="flex gap-2">
                                <Button className="h-11 flex-1" variant="outline" onClick={exportReportExcel}><FileSpreadsheet className="w-4 h-4 mr-2" />{i18n.t("batch1.a08e11119aef")}</Button>
                                <Button className="h-11 flex-1" variant="outline" onClick={exportReportPdf}><Printer className="w-4 h-4 mr-2" />{i18n.t("batch1.d613d88cb2d8")}</Button>
                            </div>
                        </div>
                    </Card>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        <Kpi label={i18n.t("batch1.52d0b35277ee")} value={reportSummary.row_count || reportRows.length || 0} testid="kpi-report-rows" />
                        <Kpi label={i18n.t("batch1.fa70e8a6cdcb")} value={formatCurrency(reportSummary.gross_total || 0, attendanceSettings?.payroll_currency || "PHP")} testid="kpi-report-gross" />
                        <Kpi label={i18n.t("batch1.72ca5ad1b7da")} value={formatCurrency(reportSummary.net_total || 0, attendanceSettings?.payroll_currency || "PHP")} testid="kpi-report-net" />
                    </div>

                    <Card className="overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-muted/50"><tr className="text-left"><PayrollTh>{i18n.t("batch1.079711ea16f3")}</PayrollTh><PayrollTh>{i18n.t("batch1.4013e76802c8")}</PayrollTh><PayrollTh>{i18n.t("batch1.9580a6176fdb")}</PayrollTh><PayrollTh>{i18n.t("batch1.9bb81c2eccbe")}</PayrollTh><PayrollTh>{i18n.t("batch1.bae7d5be7082")}</PayrollTh></tr></thead>
                            <tbody>
                                {reportRows.map((row, idx) => (
                                    <tr key={row.id || `${row.employee_id || "emp"}-${idx}`} className="border-t">
                                        <PayrollTd>{row.employee_name || row.employee || "-"}</PayrollTd>
                                        <PayrollTd>{row.date || row.period || `${year}-${String(month).padStart(2, "0")}`}</PayrollTd>
                                        <PayrollTd>{row.gross_pay == null ? "-" : formatCurrency(row.gross_pay, attendanceSettings?.payroll_currency || "PHP")}</PayrollTd>
                                        <PayrollTd>{row.net_pay == null ? "-" : formatCurrency(row.net_pay, attendanceSettings?.payroll_currency || "PHP")}</PayrollTd>
                                        <PayrollTd>{row.approval_status || row.status || "-"}</PayrollTd>
                                    </tr>
                                ))}
                                {reportRows.length === 0 && <tr><PayrollTd colSpan={5} className="text-center text-muted-foreground py-8">{i18n.t("batch1.92928c7b6205")}</PayrollTd></tr>}
                            </tbody>
                        </table>
                    </Card>
                </TabsContent>
            </Tabs>

            {dlg?.kind === "run" && <RunDialog initial={dlg.data} onSave={(b) => save("payroll-runs", b)} onClose={() => setDlg(null)} />}
            {dlg?.kind === "payslip" && (
                <PayslipDialog initial={dlg.data} employees={employees} runs={runs} onSave={(b) => save("payslips", b)} onClose={() => setDlg(null)} />
            )}
            {dlg?.kind === "calc" && <QuickCalcDialog onClose={() => setDlg(null)} />}
        </div>
    );
}

function EmployeeCompTable({ rows, currency, title, mode = "rates" }) {
    return (
        <Card className="overflow-hidden">
            <div className="p-4 font-semibold">{title}</div>
            <table className="w-full text-sm">
                <thead className="bg-muted/50"><tr className="text-left"><PayrollTh>{i18n.t("batch1.079711ea16f3")}</PayrollTh><PayrollTh>{i18n.t("batch1.29353c2b2528")}</PayrollTh><PayrollTh>{i18n.t("batch1.fe3d54900d91")}</PayrollTh><PayrollTh>{i18n.t("batch1.26ede189a778")}</PayrollTh><PayrollTh>{i18n.t("batch1.60a83b2a7fc8")}</PayrollTh><PayrollTh>{i18n.t("batch1.e144f64b4481")}</PayrollTh><PayrollTh>{i18n.t("batch1.f5dec71ed241")}</PayrollTh></tr></thead>
                <tbody>
                    {(rows || []).map((row) => (
                        <tr key={row.id} className="border-t">
                            <PayrollTd>{row.name || "-"}</PayrollTd>
                            <PayrollTd>{formatCurrency(row.monthly_salary || row.salary || 0, currency)}</PayrollTd>
                            <PayrollTd>{formatCurrency(row.daily_rate || 0, currency)}</PayrollTd>
                            <PayrollTd>{formatCurrency(row.hourly_rate || 0, currency)}</PayrollTd>
                            <PayrollTd>{mode === "deductions" ? "-" : formatCurrency(row.allowances_monthly || 0, currency)}</PayrollTd>
                            <PayrollTd>{mode === "deductions" ? "-" : formatCurrency(row.bonuses_monthly || 0, currency)}</PayrollTd>
                            <PayrollTd>{mode === "allowances" ? "-" : formatCurrency(row.other_deductions_monthly || 0, currency)}</PayrollTd>
                        </tr>
                    ))}
                </tbody>
            </table>
        </Card>
    );
}

function Kpi({ label, value, testid, tone = "slate" }) {
    const cls =
        tone === "red" ? "bg-destructive/10 border-destructive/40 text-destructive" :
        tone === "green" ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-700 dark:text-emerald-400" :
        "bg-secondary border-border";
    return (
        <Card className={`p-3 border ${cls}`} data-testid={testid}>
            <div className="tiny-label">{label}</div>
            <div className="text-2xl font-bold mt-1">{typeof value === "number" ? value : value}</div>
        </Card>
    );
}
function Field({ label, children }) { return <div><div className="text-xs text-muted-foreground mb-1">{label}</div>{children}</div>; }

function RunDialog({ initial, onSave, onClose }) {
    const monthRange = farmDateRangeForPreset("month");
    const monthStart = monthRange.from;
    const monthEnd = monthRange.to;
    const [f, setF] = useState({
        period_start: monthStart, period_end: monthEnd,
        period_type: "monthly", status: "draft", notes: "", ...initial,
    });
    const set = (k, v) => setF({ ...f, [k]: v });
    return (
        <Dialog open onOpenChange={onClose}><DialogContent className="max-w-md" data-testid="run-dialog">
            <DialogHeader><DialogTitle>{f.id ? "Edit Payroll Run" : "New Payroll Run"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                    <Field label={i18n.t("batch1.9e519416153d")}><Input type="date" value={f.period_start} onChange={(e) => set("period_start", e.target.value)} data-testid="run-start-input" /></Field>
                    <Field label={i18n.t("batch1.ff3d88f9cf07")}><Input type="date" value={f.period_end} onChange={(e) => set("period_end", e.target.value)} /></Field>
                </div>
                <Field label={i18n.t("batch1.90e239975959")}>
                    <Select value={f.period_type} onValueChange={(v) => set("period_type", v)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{PERIOD_TYPES.map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
                    </Select>
                </Field>
                <Field label={i18n.t("batch1.bae7d5be7082")}>
                    <Select value={f.status} onValueChange={(v) => set("status", v)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{["draft", "approved", "paid", "closed"].map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                    </Select>
                </Field>
                <Field label={i18n.t("batch1.70440046a3dc")}><Textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
            </div>
            <DialogFooter>
                <Button variant="outline" onClick={onClose}>{i18n.t("batch1.77dfd2135f4d")}</Button>
                <Button onClick={() => onSave(f)} data-testid="save-run-btn">{i18n.t("batch1.efc007a393f6")}</Button>
            </DialogFooter>
        </DialogContent></Dialog>
    );
}

function PayslipDialog({ initial, employees, runs, onSave, onClose }) {
    const [f, setF] = useState({
        run_id: "", employee_id: "", employee_name: "",
        gross_pay: "", allowances: 0, other_deductions: 0,
        ...initial,
    });
    const [calc, setCalc] = useState(null);
    const [busy, setBusy] = useState(false);
    const set = (k, v) => setF({ ...f, [k]: v });

    const compute = async () => {
        const gross = Number(f.gross_pay) || 0;
        if (!gross) { toast.error("Enter gross pay first"); return; }
        setBusy(true);
        try {
            const r = await api.post("/payroll/calculate", {
                gross_pay: gross,
                allowances: Number(f.allowances) || 0,
                other_deductions: Number(f.other_deductions) || 0,
                employee_id: f.employee_id || undefined,
            });
            setCalc(r.data);
        } catch (err) {
            const detail = err.response?.data?.detail;
            toast.error(detail ? `Calc failed: ${typeof detail === "string" ? detail : JSON.stringify(detail)}` : "Calc failed");
        }
        finally { setBusy(false); }
    };

    const doSave = () => {
        if (!f.employee_id) { toast.error("Employee required"); return; }
        if (!calc) { toast.error("Click Calculate first"); return; }
        const emp = employees.find(e => e.id === f.employee_id);
        onSave({
            ...f,
            employee_name: emp?.name || f.employee_name,
            gross_pay: Number(f.gross_pay) || 0,
            allowances: Number(f.allowances) || 0,
            other_deductions: Number(f.other_deductions) || 0,
            taxable_income: calc.taxable_income,
            sss_employee: calc.contributions.sss.employee,
            sss_employer: calc.contributions.sss.employer,
            philhealth_employee: calc.contributions.philhealth.employee,
            philhealth_employer: calc.contributions.philhealth.employer,
            pagibig_employee: calc.contributions.pagibig.employee,
            pagibig_employer: calc.contributions.pagibig.employer,
            withholding_tax: calc.withholding_tax,
            total_deductions: calc.total_deductions,
            employer_contributions: calc.contributions.employer_total,
            net_pay: calc.net_pay,
            thirteenth_month_accrual: calc.thirteenth_month_accrual,
        });
    };

    return (
        <Dialog open onOpenChange={onClose}><DialogContent className="max-w-2xl" data-testid="payslip-dialog">
            <DialogHeader>
                <DialogTitle>{f.id ? "Edit Payslip" : "New Payslip"}</DialogTitle>
                <DialogDescription>{i18n.t("batch1.d34807437ad4")}</DialogDescription>
            </DialogHeader>
            <div className="grid md:grid-cols-2 gap-4 max-h-[65vh] overflow-y-auto pr-1">
                <div className="space-y-3">
                    <Field label={i18n.t("batch1.01f09d033582")}>
                        <Select value={f.employee_id} onValueChange={(v) => set("employee_id", v)}>
                            <SelectTrigger data-testid="payslip-employee-select"><SelectValue placeholder={i18n.t("batch1.c3b69e661eec")} /></SelectTrigger>
                            <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                        </Select>
                    </Field>
                    <Field label={i18n.t("batch1.35588aa2c835")}>
                        <Select value={f.run_id || "__none__"} onValueChange={(v) => set("run_id", v === "__none__" ? "" : v)}>
                            <SelectTrigger><SelectValue placeholder={i18n.t("batch1.3b906ae77c48")} /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__none__">{i18n.t("batch1.3b906ae77c48")}</SelectItem>
                                {runs.map(r => <SelectItem key={r.id} value={r.id}>{r.period_start} → {r.period_end}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </Field>
                    <Field label={i18n.t("batch1.3198fe550382")}>
                        <Input type="number" step="0.01" value={f.gross_pay} onChange={(e) => set("gross_pay", e.target.value)} data-testid="payslip-gross-input" placeholder={i18n.t("batch1.0755a0dcc6b2")} />
                    </Field>
                    <div className="grid grid-cols-2 gap-2">
                        <Field label={i18n.t("batch1.99f0f68fdb03")}><Input type="number" step="0.01" value={f.allowances} onChange={(e) => set("allowances", e.target.value)} /></Field>
                        <Field label={i18n.t("batch1.f831df50d6d4")}><Input type="number" step="0.01" value={f.other_deductions} onChange={(e) => set("other_deductions", e.target.value)} /></Field>
                    </div>
                    <Button onClick={compute} disabled={busy} className="w-full" variant="outline" data-testid="calculate-btn">
                        <Calculator className="w-4 h-4 mr-1" />{busy ? "Calculating…" : "Calculate PH deductions"}
                    </Button>
                </div>

                <Card className="p-3 bg-secondary/40 space-y-1.5 text-sm" data-testid="payslip-breakdown">
                    {!calc && <div className="text-center text-muted-foreground py-8">{i18n.t("batch1.79cbc5eb672a")} <b>{i18n.t("batch1.8e5903a8e4db")}</b> {i18n.t("batch1.554a6cb53109")}</div>}
                    {calc && <>
                        <Row label={i18n.t("batch1.3843ff8c30a8")} value={formatCurrency(calc.gross_pay)} />
                        <Row label={i18n.t("batch1.60a83b2a7fc8")} value={formatCurrency(calc.allowances)} />
                        <div className="border-t my-1"></div>
                        <div className="text-xs uppercase tracking-wider text-muted-foreground mt-1">{i18n.t("batch1.6f919fae4c76")}</div>
                        <Row label={`SSS 5% (MSC ${formatCurrency(calc.contributions.sss.msc)})`} value={"-" + formatCurrency(calc.contributions.sss.employee)} red />
                        <Row label={i18n.t("batch1.2f9a6ef20587")} value={"-" + formatCurrency(calc.contributions.philhealth.employee)} red />
                        <Row label={i18n.t("batch1.dbded5c557db")} value={"-" + formatCurrency(calc.contributions.pagibig.employee)} red />
                        <Row label={i18n.t("batch1.40a3ddbd053e")} value={"-" + formatCurrency(calc.withholding_tax)} red />
                        <Row label={i18n.t("batch1.f831df50d6d4")} value={"-" + formatCurrency(calc.other_deductions)} red />
                        <div className="border-t my-1"></div>
                        <Row label={i18n.t("batch1.5d9129ed27ec")} value={formatCurrency(calc.total_deductions)} bold red />
                        <Row label={i18n.t("batch1.2fe22cb4982c")} value={formatCurrency(calc.net_pay)} bold big />
                        <div className="border-t my-1"></div>
                        <Row label={i18n.t("batch1.0bc42f0bd0f1")} value={formatCurrency(calc.contributions.employer_total)} muted />
                        <Row label={i18n.t("batch1.573c15f22edb")} value={formatCurrency(calc.thirteenth_month_accrual)} muted />
                    </>}
                </Card>
            </div>
            <DialogFooter>
                <Button variant="outline" onClick={onClose}>{i18n.t("batch1.77dfd2135f4d")}</Button>
                <Button onClick={doSave} data-testid="save-payslip-btn" disabled={!calc}>{i18n.t("batch1.d7b19b13afed")}</Button>
            </DialogFooter>
        </DialogContent></Dialog>
    );
}

function QuickCalcDialog({ onClose }) {
    const [gross, setGross] = useState("");
    const [calc, setCalc] = useState(null);
    const [busy, setBusy] = useState(false);
    const run = async () => {
        const g = Number(gross) || 0;
        if (!g) return;
        setBusy(true);
        try {
            const r = await api.post("/payroll/calculate", { gross_pay: g });
            setCalc(r.data);
        } catch (e) { toast.error("Failed"); }
        finally { setBusy(false); }
    };
    return (
        <Dialog open onOpenChange={onClose}><DialogContent className="max-w-md" data-testid="quick-calc-dialog">
            <DialogHeader><DialogTitle>{i18n.t("batch1.7d8b22804b5f")}</DialogTitle><DialogDescription>{i18n.t("batch1.58abf77397d3")}</DialogDescription></DialogHeader>
            <div className="space-y-3">
                <Field label={i18n.t("batch1.2602fe1c7df1")}>
                    <Input type="number" value={gross} onChange={(e) => setGross(e.target.value)} placeholder={i18n.t("batch1.0755a0dcc6b2")} data-testid="quick-gross-input" onKeyDown={(e) => e.key === "Enter" && run()} />
                </Field>
                <Button onClick={run} disabled={busy || !gross} className="w-full" data-testid="quick-run-btn">
                    <Calculator className="w-4 h-4 mr-1" />{busy ? "…" : "Calculate"}
                </Button>
                {calc && <Card className="p-3 bg-secondary/40 space-y-1 text-sm">
                    <Row label={i18n.t("batch1.9580a6176fdb")} value={formatCurrency(calc.gross_pay)} />
                    <Row label={i18n.t("batch1.f5dc67c95488")} value={"-" + formatCurrency(calc.contributions.sss.employee)} red />
                    <Row label={i18n.t("batch1.be3d534838ef")} value={"-" + formatCurrency(calc.contributions.philhealth.employee)} red />
                    <Row label={i18n.t("batch1.e148523dfcef")} value={"-" + formatCurrency(calc.contributions.pagibig.employee)} red />
                    <Row label={i18n.t("batch1.856cf336058f")} value={"-" + formatCurrency(calc.withholding_tax)} red />
                    <div className="border-t"></div>
                    <Row label={i18n.t("batch1.9bb81c2eccbe")} value={formatCurrency(calc.net_pay)} bold big />
                </Card>}
            </div>
        </DialogContent></Dialog>
    );
}

function Row({ label, value, red, bold, big, muted }) {
    return (
        <div className={`flex justify-between ${bold ? "font-bold" : ""} ${big ? "text-lg" : ""} ${muted ? "text-muted-foreground text-xs" : ""}`}>
            <span>{label}</span>
            <span className={red ? "text-destructive" : big ? "text-emerald-600 dark:text-emerald-400" : ""}>{value}</span>
        </div>
    );
}

function printPayrollSlip(row) {
    try {
        const doc = new jsPDF();
        doc.setFontSize(18); doc.text("Payroll Review Slip", 14, 20);
        doc.setFontSize(11);
        let y = 32;
        doc.text(`Employee: ${row.employee_name || "—"}`, 14, y); y += 7;
        const rows = [
            ["Regular hours", hoursLabel(row.regular_hours)],
            ["Approved overtime", hoursLabel(row.approved_overtime_hours || row.overtime_hours)],
            ["Gross pay", Number(row.gross_pay || 0)],
            ["Late deduction", -(row.late_deduction || 0)],
            ["Absent deduction", -(row.absent_deduction || 0)],
            ["Other deductions", -(row.other_deductions || 0)],
        ];
        rows.forEach(([label, val]) => {
            doc.text(label, 14, y);
            doc.text(`PHP ${Number(val || 0).toFixed(2)}`, 180, y, { align: "right" });
            y += 6;
        });
        y += 4;
        doc.setFontSize(14);
        doc.text("Net Pay", 14, y);
        doc.text(`PHP ${Number(row.net_pay || 0).toFixed(2)}`, 180, y, { align: "right" });
        doc.save(`payroll-review-${(row.employee_name || "employee").replace(/\s+/g, "_")}.pdf`);
    } catch (e) { toast.error("PDF failed"); }
}

function printPayslip(p, emp, run) {
    try {
        const doc = new jsPDF();
        doc.setFontSize(18); doc.text("Payslip", 14, 20);
        doc.setFontSize(11);
        let y = 32;
        doc.text(`Employee: ${emp?.name || p.employee_name || "—"}`, 14, y); y += 7;
        if (run) { doc.text(`Period: ${run.period_start} to ${run.period_end}`, 14, y); y += 7; }
        y += 4;
        const rows = [
            ["Gross pay", p.gross_pay],
            ["Allowances", p.allowances],
            ["SSS", -(p.sss_employee || 0)],
            ["PhilHealth", -(p.philhealth_employee || 0)],
            ["Pag-IBIG", -(p.pagibig_employee || 0)],
            ["Withholding tax", -(p.withholding_tax || 0)],
            ["Other deductions", -(p.other_deductions || 0)],
            ["Total deductions", -(p.total_deductions || 0)],
        ];
        rows.forEach(([label, val]) => {
            doc.text(label, 14, y);
            doc.text(`PHP ${Number(val || 0).toFixed(2)}`, 180, y, { align: "right" });
            y += 6;
        });
        y += 4;
        doc.setFontSize(14);
        doc.text("Net Pay", 14, y);
        doc.text(`PHP ${Number(p.net_pay || 0).toFixed(2)}`, 180, y, { align: "right" });
        doc.save(`payslip-${(emp?.name || "employee").replace(/\s+/g, "_")}.pdf`);
    } catch (e) { toast.error("PDF failed"); }
}
